<template>
  <div class="results4-wrap">

    <div class="topbar">
      <div class="topbar-inner">
        <div class="brand">
          <div class="brand-mark"></div>
          <div class="brand-text">
            SEQ
            <small>Swiss Eating Quiz</small>
          </div>
        </div>
        <div class="topbar-meta">REPORT · 09.05.2026 · ID 2841-CH</div>
      </div>
    </div>

    <header class="hero">
      <div class="container">
        <div class="hero-grid">
          <div>
            <div class="hero-eyebrow">Personal Dietary Report</div>
            <h1 class="hero-title">How your eating habits compare to the Swiss <em>recommendations</em>.</h1>
            <p class="hero-lede">
              A look at how often you eat from each of the seven food groups,
              set against the Schweizer Lebensmittelpyramide. Some patterns
              will please you, others may surprise you.
            </p>
            <div class="hero-meta">
              <span>Quiz completed<strong>9 May 2026</strong></span>
              <span>Questions answered<strong>67</strong></span>
              <span>Time taken<strong>9 minutes</strong></span>
            </div>
          </div>

          <div class="score-card">
            <div class="score-label">Your overall score</div>
            <div class="score-value">76<sub>/100</sub></div>
            <div class="score-status">Solide Basis</div>

            <svg class="score-svg" viewBox="0 0 360 80" preserveAspectRatio="xMidYMid meet">
              <defs>
                <linearGradient id="gauge-grad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stop-color="#C8392E"/>
                  <stop offset="40%" stop-color="#E8A33D"/>
                  <stop offset="70%" stop-color="#85BB2F"/>
                  <stop offset="100%" stop-color="#2D8659"/>
                </linearGradient>
              </defs>
              <g stroke="#9C9C9C" stroke-width="0.5">
                <line x1="20" y1="44" x2="20" y2="50"/>
                <line x1="105" y1="44" x2="105" y2="50"/>
                <line x1="190" y1="44" x2="190" y2="50"/>
                <line x1="275" y1="44" x2="275" y2="50"/>
                <line x1="340" y1="44" x2="340" y2="50"/>
              </g>
              <g font-family="JetBrains Mono, monospace" font-size="8" fill="#9C9C9C">
                <text x="20" y="62" text-anchor="middle">0</text>
                <text x="105" y="62" text-anchor="middle">25</text>
                <text x="190" y="62" text-anchor="middle">50</text>
                <text x="275" y="62" text-anchor="middle">75</text>
                <text x="340" y="62" text-anchor="middle">100</text>
              </g>
              <rect x="20" y="32" width="320" height="10" fill="url(#gauge-grad)"/>
              <g transform="translate(263.2 37)">
                <polygon points="0,-12 9,0 0,12 -9,0" fill="#DA291C" stroke="white" stroke-width="2"/>
                <text x="0" y="3" text-anchor="middle" font-family="Inter, sans-serif" font-size="9" font-weight="600" fill="white">76</text>
              </g>
            </svg>
            <div class="score-legend">
              <span>Needs work</span>
              <span>Outstanding</span>
            </div>
          </div>
        </div>
      </div>
    </header>

    <section class="report-section">
      <div class="container">
        <div class="section-header">
          <div class="section-num">02 / 06</div>
          <div class="section-title-block">
            <h2>Your seven food <em>groups</em>, at a glance.</h2>
            <p class="section-lede">
              The Swiss Eating Quiz measures how often you eat from each of
              the seven food groups. The radar below shows the relative
              frequency of your consumption against the Swiss recommendation.
              Where the red line sits inside the green shape, you eat less
              often than recommended. Where it sits outside, you eat more
              often.
            </p>
          </div>
        </div>

        <div class="wide-narrow">
          <div class="card">
            <div class="card-eyebrow">Fig. 1 · Frequency by food group</div>
            <div class="card-title">You vs. Swiss recommendation</div>
            <p class="card-sub">
              Seven axes, one polygon per profile. Closer to the edge means
              more frequent consumption.
            </p>
            <div class="radar-wrap">
              <svg viewBox="0 0 400 400" class="radar-svg" :aria-label="'Radar comparing your scores to Swiss recommendation across 7 food groups'">
                <circle
                  v-for="r in [25, 50, 75, 100]"
                  :key="`ring-${r}`"
                  cx="200"
                  cy="200"
                  :r="(r / 100) * radar.radius"
                  fill="none"
                  stroke="#E5E0D7"
                  stroke-width="1"
                />
                <line
                  v-for="(p, i) in radar.axes"
                  :key="`ax-${i}`"
                  x1="200"
                  y1="200"
                  :x2="p.x"
                  :y2="p.y"
                  stroke="#E5E0D7"
                  stroke-width="1"
                />
                <polygon
                  :points="radar.recPolygon"
                  fill="rgba(45, 134, 89, 0.15)"
                  stroke="#2D8659"
                  stroke-width="2"
                />
                <polygon
                  :points="radar.youPolygon"
                  fill="rgba(218, 41, 28, 0.12)"
                  stroke="#DA291C"
                  stroke-width="2"
                  stroke-dasharray="6 3"
                />
                <circle
                  v-for="(p, i) in radar.recPoints"
                  :key="`rec-${i}`"
                  :cx="p.x"
                  :cy="p.y"
                  r="4"
                  fill="#2D8659"
                />
                <circle
                  v-for="(p, i) in radar.youPoints"
                  :key="`you-${i}`"
                  :cx="p.x"
                  :cy="p.y"
                  r="4"
                  fill="#DA291C"
                />
                <text
                  v-for="(p, i) in radar.labels"
                  :key="`lb-${i}`"
                  :x="p.x"
                  :y="p.y"
                  text-anchor="middle"
                  dominant-baseline="middle"
                  font-size="12"
                  font-weight="500"
                  fill="#2D2D2D"
                >{{ p.label }}</text>
              </svg>
            </div>
            <div class="radar-legend">
              <span class="legend-item"><span class="legend-dot legend-rec"></span>Swiss recommendation</span>
              <span class="legend-item"><span class="legend-dot legend-you"></span>You</span>
            </div>
          </div>

          <div class="card">
            <div class="card-eyebrow">Snapshot</div>
            <div class="card-title">In numbers</div>
            <div class="stat-row">
              <div class="stat">
                <div class="stat-label">Strongest group</div>
                <div class="stat-value good">90<span class="stat-unit">/100 · Drinks</span></div>
              </div>
            </div>
            <div class="stat-row">
              <div class="stat">
                <div class="stat-label">Weakest group</div>
                <div class="stat-value warn">45<span class="stat-unit">/100 · Plant proteins</span></div>
              </div>
            </div>
            <div class="stat-row">
              <div class="stat">
                <div class="stat-label">Groups above target</div>
                <div class="stat-value">3<span class="stat-unit">of 7</span></div>
              </div>
              <div class="stat">
                <div class="stat-label">Groups below target</div>
                <div class="stat-value">2<span class="stat-unit">of 7</span></div>
              </div>
            </div>
            <p class="pull">
              Your strongest pattern: water, vegetables, fruit. Your biggest
              gap: plant proteins like Hülsenfrüchte and Tofu.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="report-section">
      <div class="container">
        <div class="section-header">
          <div class="section-num">03 / 06</div>
          <div class="section-title-block">
            <h2>Where you sit on the <em>Lebensmittelpyramide</em>.</h2>
            <p class="section-lede">
              The Schweizer Lebensmittelpyramide groups foods into six tiers,
              from drinks at the base to discretionary foods at the top. Each
              tier below is shaded by your status: green where you meet the
              recommendation, amber where you are slightly off, red where you
              are clearly above or below.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Fig. 2 · Schweizer Lebensmittelpyramide</div>
          <div class="card-title">Your seven food groups, mapped to the pyramid tiers</div>
          <p class="card-sub">
            The pyramid is the official Swiss reference for dietary balance.
            The seven SEQ groups map onto these tiers.
          </p>

          <svg class="pyramid-svg" viewBox="0 0 600 360" xmlns="http://www.w3.org/2000/svg">
            <polygon points="20,330 580,330 555,295 45,295" fill="#B8D8C5" stroke="#1A1A1A" stroke-width="1"/>
            <text x="300" y="316" text-anchor="middle" font-family="Inter, sans-serif" font-size="13" font-weight="500" fill="#1A1A1A">Drinks · water, unsweetened tea</text>
            <text x="555" y="316" text-anchor="end" font-family="JetBrains Mono, monospace" font-size="10" fill="#2D8659">✓ 90 / 100</text>

            <polygon points="45,295 555,295 530,255 70,255" fill="#B8D8C5" stroke="#1A1A1A" stroke-width="1"/>
            <text x="300" y="278" text-anchor="middle" font-family="Inter, sans-serif" font-size="13" font-weight="500" fill="#1A1A1A">Vegetables and fruit</text>
            <text x="530" y="278" text-anchor="end" font-family="JetBrains Mono, monospace" font-size="10" fill="#2D8659">✓ 88 · 82 / 100</text>

            <polygon points="70,255 530,255 505,210 95,210" fill="#F5D9A8" stroke="#1A1A1A" stroke-width="1"/>
            <text x="300" y="235" text-anchor="middle" font-family="Inter, sans-serif" font-size="13" font-weight="500" fill="#1A1A1A">Grains, potatoes, plant proteins</text>
            <text x="505" y="235" text-anchor="end" font-family="JetBrains Mono, monospace" font-size="10" fill="#E8A33D">↓ 65 · 45 / 100</text>

            <polygon points="95,210 505,210 480,165 120,165" fill="#B8D8C5" stroke="#1A1A1A" stroke-width="1"/>
            <text x="300" y="190" text-anchor="middle" font-family="Inter, sans-serif" font-size="13" font-weight="500" fill="#1A1A1A">Dairy, animal proteins</text>
            <text x="480" y="190" text-anchor="end" font-family="JetBrains Mono, monospace" font-size="10" fill="#2D8659">✓ 78 · 70 / 100</text>

            <polygon points="120,165 480,165 460,120 140,120" fill="#F2EDE4" stroke="#1A1A1A" stroke-width="1"/>
            <text x="300" y="145" text-anchor="middle" font-family="Inter, sans-serif" font-size="13" font-weight="500" fill="#1A1A1A">Oils, fats, nuts</text>
            <text x="460" y="145" text-anchor="end" font-family="JetBrains Mono, monospace" font-size="10" fill="#9C9C9C">not measured</text>

            <polygon points="140,120 460,120 300,40" fill="#F2EDE4" stroke="#1A1A1A" stroke-width="1"/>
            <text x="300" y="92" text-anchor="middle" font-family="Inter, sans-serif" font-size="13" font-weight="500" fill="#1A1A1A">Sweets, snacks, alcohol</text>
            <text x="300" y="108" text-anchor="middle" font-family="JetBrains Mono, monospace" font-size="10" fill="#9C9C9C">not measured in v1</text>
          </svg>

          <p class="pull">
            The base of the pyramid (drinks, vegetables, fruit) is your
            strength. The middle (grains and plant proteins) is where you have
            the most room to grow.
          </p>
        </div>
      </div>
    </section>

    <section class="report-section">
      <div class="container">
        <div class="section-header">
          <div class="section-num">04 / 06</div>
          <div class="section-title-block">
            <h2>Group by <em>group</em>, in detail.</h2>
            <p class="section-lede">
              A short note on each of the seven groups. The score reflects how
              closely your reported frequency matches the Swiss recommendation.
              The commentary reflects what your answers suggest about your
              habits, not a clinical assessment.
            </p>
          </div>
        </div>

        <div class="group-list">
          <div v-for="g in groups" :key="g.name" class="group-card">
            <div class="group-card-header">
              <span class="group-emoji">{{ g.emoji }}</span>
              <span class="group-name">{{ g.name }}</span>
              <span class="group-score" :class="g.status">{{ g.score }} / 100</span>
            </div>
            <div class="group-bar">
              <div
                class="group-bar-fill"
                :class="g.status"
                :style="{
                  width: g.score + '%',
                  ...(g.status === 'neutral' ? { background: '#4A6FA5' } : {}),
                }"
              ></div>
            </div>
            <p class="group-feedback">{{ g.feedback }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="report-section">
      <div class="container">
        <div class="section-header">
          <div class="section-num">05 / 06</div>
          <div class="section-title-block">
            <h2>What counts as a <em>portion</em>.</h2>
            <p class="section-lede">
              A reference, in case it helps when reading the rest of the
              report. These are the standard portion sizes used by the
              Schweizerische Gesellschaft für Ernährung (SGE). The SEQ uses
              these definitions throughout.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Reference</div>
          <div class="card-title">Standard Swiss portions</div>
          <p class="card-sub">
            From the SGE Schweizer Lebensmittelpyramide. Quantities are
            approximate and intended as everyday guides, not precise
            measurements.
          </p>

          <div class="portion-grid">
            <div v-for="p in portions" :key="p.name" class="portion-item">
              <span class="portion-amount">{{ p.amount }}</span>
              <span class="portion-name">{{ p.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="report-section warm">
      <div class="container">
        <div class="section-header">
          <div class="section-num">06 / 06</div>
          <div class="section-title-block">
            <h2>Three changes <em>worth</em> making.</h2>
            <p class="section-lede">
              From your seven group scores, these three small, specific changes
              would move your overall score the most. Each is calibrated to
              your reported habits, not to a generic ideal. None requires
              giving anything up.
            </p>
          </div>
        </div>

        <div class="recs-grid">

          <div class="rec-card">
            <div class="rec-priority">High impact</div>
            <svg class="rec-icon" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="20" stroke="#DA291C" stroke-width="1.5"/>
              <path d="M16 24L22 30L34 18" stroke="#DA291C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="rec-title">Add Hülsenfrüchte two times a week.</h3>
            <p class="rec-body">
              Plant proteins are your weakest group at 45 / 100. A lentil
              salad for lunch, a chickpea curry for dinner, or hummus on bread,
              two or three times a week, would close most of the gap.
            </p>
            <div class="rec-impact">Estimated score lift: <strong>+8 points</strong></div>
          </div>

          <div class="rec-card">
            <div class="rec-priority medium">Medium impact</div>
            <svg class="rec-icon" viewBox="0 0 48 48" fill="none">
              <rect x="8" y="14" width="32" height="24" rx="2" stroke="#E8A33D" stroke-width="1.5"/>
              <path d="M16 22V30M24 18V30M32 22V30" stroke="#E8A33D" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <h3 class="rec-title">Switch white bread for wholemeal.</h3>
            <p class="rec-body">
              Your grains score sits at 65 / 100, mostly because the share of
              whole-grain products is low. A simple swap at the next shop, no
              recipe changes, would lift this group meaningfully.
            </p>
            <div class="rec-impact">Estimated score lift: <strong>+5 points</strong></div>
          </div>

          <div class="rec-card">
            <div class="rec-priority low">Easy win</div>
            <svg class="rec-icon" viewBox="0 0 48 48" fill="none">
              <path d="M24 8C18 8 14 14 14 20C14 28 24 40 24 40C24 40 34 28 34 20C34 14 30 8 24 8Z" stroke="#2D8659" stroke-width="1.5" stroke-linejoin="round"/>
              <circle cx="24" cy="20" r="3" stroke="#2D8659" stroke-width="1.5"/>
            </svg>
            <h3 class="rec-title">One fish meal per week.</h3>
            <p class="rec-body">
              Your animal protein score is 70 / 100, with most points coming
              from meat. A single weekly portion of fish, whether fresh, smoked
              or from a tin, would round the group out.
            </p>
            <div class="rec-impact">Estimated score lift: <strong>+3 points</strong></div>
          </div>

        </div>

        <p class="placeholder-note">Placeholder text. Final wording will be provided by the Ernährungsteam.</p>
      </div>
    </section>

    <footer class="report-footer">
      <div class="container">
        <div class="footer-inner">
          <div class="footer-brand">
            <div class="brand">
              <div class="brand-mark"></div>
              <div class="brand-text">
                SEQ
                <small>Swiss Eating Quiz</small>
              </div>
            </div>
            <p>
              The SEQ is a research instrument developed at Berner
              Fachhochschule for measuring how Swiss residents' eating habits
              compare to the Schweizer Lebensmittelpyramide. This report is
              not a clinical assessment.
            </p>
          </div>
          <div class="footer-meta">
            Demo report · Mock data<br>
            v0.1 · May 2026<br>
            BFH Departement Gesundheit
          </div>
        </div>
      </div>
    </footer>

  </div>
</template>

<script>
const RADAR_CENTER = 200;
const RADAR_RADIUS = 130;
const LABEL_OFFSET = 26;

function angleAt(i, n) {
  return (i / n) * 2 * Math.PI - Math.PI / 2;
}

export default {
  name: 'Results4',
  data() {
    return {
      radarLabels: ['Vegetables', 'Fruit', 'Animal proteins', 'Plant proteins', 'Grains', 'Dairy', 'Drinks'],
      recommendation: [85, 85, 75, 75, 80, 75, 90],
      youData: [88, 82, 70, 45, 65, 78, 90],
      groups: [
        { emoji: '🥬', name: 'Vegetables', score: 88, status: 'good',
          feedback: 'You eat vegetables regularly and use a good variety. This is one of the strongest pillars of a balanced diet, and you are getting it right.' },
        { emoji: '🍎', name: 'Fruit', score: 82, status: 'good',
          feedback: 'Solid fruit consumption. Adding more seasonal variety across the year would round the picture out further.' },
        { emoji: '💧', name: 'Drinks', score: 90, status: 'good',
          feedback: 'You drink enough water and avoid sweetened drinks. Excellent.' },
        { emoji: '🥛', name: 'Dairy', score: 78, status: 'good',
          feedback: 'Good consumption, with a sensible mix of unsweetened varieties. Yoghurt and fresh cheese in particular show well.' },
        { emoji: '🥩', name: 'Animal proteins', score: 70, status: 'neutral',
          feedback: 'A balanced consumption of meat and fish. A little more fish would lift this further, especially fatty fish once a week.' },
        { emoji: '🌾', name: 'Grains', score: 65, status: 'neutral',
          feedback: 'Reasonable, but the share of whole-grain products could be higher. A switch from white bread to wholemeal is the simplest single change.' },
        { emoji: '🌱', name: 'Plant proteins', score: 45, status: 'warn',
          feedback: 'This is your largest gap. Hülsenfrüchte, Tofu and other plant proteins appear rarely in your week. Even one or two additions per week would move this score noticeably.' },
      ],
      portions: [
        { amount: '120 g', name: 'Vegetables or fruit, roughly a handful' },
        { amount: '2 dl', name: 'Milk or unsweetened soy drink, one regular glass' },
        { amount: '30 g', name: 'Hard cheese, two slices or one thick piece' },
        { amount: '25–30 g', name: 'Nuts, a small handful' },
        { amount: '150–200 g', name: 'Yoghurt, one regular pot' },
        { amount: '60–80 g', name: 'Pulses, dry weight (about 150 g cooked)' },
      ],
    };
  },
  computed: {
    radar() {
      const n = this.radarLabels.length;
      const axes = this.radarLabels.map((_, i) => {
        const a = angleAt(i, n);
        return {
          x: RADAR_CENTER + RADAR_RADIUS * Math.cos(a),
          y: RADAR_CENTER + RADAR_RADIUS * Math.sin(a),
        };
      });
      const labels = this.radarLabels.map((label, i) => {
        const a = angleAt(i, n);
        return {
          x: RADAR_CENTER + (RADAR_RADIUS + LABEL_OFFSET) * Math.cos(a),
          y: RADAR_CENTER + (RADAR_RADIUS + LABEL_OFFSET) * Math.sin(a),
          label,
        };
      });
      const project = (data) => data.map((value, i) => {
        const a = angleAt(i, n);
        const r = (value / 100) * RADAR_RADIUS;
        return {
          x: RADAR_CENTER + r * Math.cos(a),
          y: RADAR_CENTER + r * Math.sin(a),
        };
      });
      const recPoints = project(this.recommendation);
      const youPoints = project(this.youData);
      return {
        radius: RADAR_RADIUS,
        axes,
        labels,
        recPoints,
        youPoints,
        recPolygon: recPoints.map(p => `${p.x},${p.y}`).join(' '),
        youPolygon: youPoints.map(p => `${p.x},${p.y}`).join(' '),
      };
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

.results4-wrap {
  --swiss-red: #DA291C;
  --swiss-red-deep: #B01E13;
  --ink: #1A1A1A;
  --ink-soft: #2D2D2D;
  --ink-mute: #6B6B6B;
  --ink-faint: #9C9C9C;
  --paper: #FAF7F2;
  --paper-warm: #F2EDE4;
  --paper-card: #FFFFFF;
  --rule: #1A1A1A;
  --rule-soft: #E5E0D7;
  --good: #2D8659;
  --good-soft: #B8D8C5;
  --warn: #E8A33D;
  --warn-soft: #F5D9A8;
  --bad: #C8392E;
  --bad-soft: #F0C2BD;
  --neutral: #4A6FA5;
  --accent: #534AB7;
  --font-display: "Fraunces", Georgia, serif;
  --font-body: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: "JetBrains Mono", "SF Mono", Consolas, monospace;

  font-family: var(--font-body);
  background: var(--paper);
  color: var(--ink);
  line-height: 1.55;
  font-size: 16px;
  -webkit-font-smoothing: antialiased;
  min-height: 100%;
}

.results4-wrap *,
.results4-wrap *::before,
.results4-wrap *::after {
  box-sizing: border-box;
}

.topbar {
  border-bottom: 1px solid var(--ink);
  background: rgba(250, 247, 242, 0.94);
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(8px);
}
.topbar-inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: 14px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 18px;
  letter-spacing: -0.01em;
}
.brand-mark {
  width: 28px;
  height: 28px;
  background: var(--swiss-red);
  position: relative;
  flex-shrink: 0;
}
.brand-mark::before,
.brand-mark::after {
  content: "";
  position: absolute;
  background: white;
}
.brand-mark::before {
  top: 50%;
  left: 22%;
  right: 22%;
  height: 4px;
  transform: translateY(-50%);
}
.brand-mark::after {
  left: 50%;
  top: 22%;
  bottom: 22%;
  width: 4px;
  transform: translateX(-50%);
}
.brand-text {
  display: flex;
  flex-direction: column;
}
.brand-text small {
  font-family: var(--font-body);
  font-weight: 400;
  font-size: 11px;
  color: var(--ink-mute);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-top: -2px;
}
.topbar-meta {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--ink-mute);
}

.container {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 32px;
}

.hero {
  padding: 80px 0 64px;
  border-bottom: 1px solid var(--ink);
}
.hero-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 64px;
  align-items: end;
}
.hero-eyebrow {
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--swiss-red);
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.hero-eyebrow::before {
  content: "";
  width: 24px;
  height: 1px;
  background: var(--swiss-red);
}
.hero-title {
  font-family: var(--font-display);
  font-size: clamp(48px, 6vw, 84px);
  font-weight: 400;
  line-height: 0.95;
  letter-spacing: -0.03em;
  margin: 0 0 24px;
  font-variation-settings: "opsz" 144;
}
.hero-title em {
  font-style: italic;
  font-weight: 300;
  color: var(--swiss-red);
}
.hero-lede {
  font-size: 18px;
  line-height: 1.55;
  color: var(--ink-soft);
  max-width: 480px;
  margin: 0 0 32px;
}
.hero-meta {
  display: flex;
  gap: 32px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--ink-mute);
}
.hero-meta span {
  display: flex;
  flex-direction: column;
}
.hero-meta strong {
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--ink);
  margin-top: 2px;
  font-weight: 500;
}

.score-card {
  background: var(--paper-card);
  border: 1px solid var(--ink);
  padding: 36px;
  position: relative;
}
.score-card::before {
  content: "01";
  position: absolute;
  top: -1px;
  right: -1px;
  background: var(--ink);
  color: var(--paper);
  font-family: var(--font-mono);
  font-size: 11px;
  padding: 6px 10px;
  letter-spacing: 0.08em;
}
.score-label {
  font-family: var(--font-mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-mute);
  margin-bottom: 8px;
}
.score-value {
  font-family: var(--font-display);
  font-size: 96px;
  font-weight: 300;
  line-height: 1;
  letter-spacing: -0.04em;
  color: var(--ink);
}
.score-value sub {
  font-size: 32px;
  color: var(--ink-faint);
  vertical-align: baseline;
  margin-left: 4px;
}
.score-status {
  display: inline-block;
  margin-top: 12px;
  padding: 4px 10px;
  background: var(--good-soft);
  color: #1A4A30;
  font-family: var(--font-mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.score-svg {
  margin-top: 24px;
  width: 100%;
  height: auto;
}
.score-legend {
  display: flex;
  justify-content: space-between;
  font-family: var(--font-mono);
  font-size: 10px;
  color: var(--ink-mute);
  margin-top: 8px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.report-section {
  padding: 80px 0;
  border-bottom: 1px solid var(--rule-soft);
}
.report-section.warm {
  background: var(--paper-warm);
}
.section-header {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 24px;
  margin-bottom: 48px;
  align-items: start;
}
.section-num {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--swiss-red);
  letter-spacing: 0.1em;
  padding-top: 8px;
  border-top: 2px solid var(--swiss-red);
}
.section-title-block h2 {
  font-family: var(--font-display);
  font-size: 42px;
  font-weight: 400;
  line-height: 1.05;
  letter-spacing: -0.02em;
  margin: 0 0 16px;
  font-variation-settings: "opsz" 144;
}
.section-title-block h2 em {
  font-style: italic;
  color: var(--swiss-red);
}
.section-lede {
  font-size: 17px;
  line-height: 1.6;
  color: var(--ink-soft);
  max-width: 720px;
  margin: 0;
}

.card {
  background: var(--paper-card);
  border: 1px solid var(--rule-soft);
  padding: 32px;
  position: relative;
}
.card-eyebrow {
  font-family: var(--font-mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-mute);
  margin-bottom: 8px;
}
.card-title {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 500;
  letter-spacing: -0.01em;
  margin-bottom: 8px;
}
.card-sub {
  font-size: 14px;
  color: var(--ink-mute);
  margin: 0 0 24px;
}

.wide-narrow {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 24px;
}

.stat-row {
  display: flex;
  gap: 32px;
  padding: 20px 0;
  border-top: 1px solid var(--rule-soft);
}
.stat-row:first-of-type {
  border-top: none;
  padding-top: 0;
}
.stat {
  flex: 1;
}
.stat-label {
  font-family: var(--font-mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-mute);
  margin-bottom: 4px;
}
.stat-value {
  font-family: var(--font-display);
  font-size: 32px;
  font-weight: 400;
  letter-spacing: -0.02em;
  color: var(--ink);
}
.stat-value.good { color: var(--good); }
.stat-value.warn { color: var(--warn); }
.stat-value.bad { color: var(--bad); }
.stat-unit {
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--ink-mute);
  font-weight: 400;
  margin-left: 4px;
}

.radar-wrap {
  width: 100%;
  display: flex;
  justify-content: center;
}
.radar-svg {
  width: 100%;
  max-width: 400px;
  height: auto;
}
.radar-legend {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 16px;
  font-size: 13px;
  color: var(--ink-soft);
}
.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}
.legend-dot.legend-rec { background: #2D8659; }
.legend-dot.legend-you { background: #DA291C; }

.pyramid-svg {
  width: 100%;
  height: auto;
  max-height: 420px;
  margin-top: 16px;
}

.group-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 32px;
}
.group-card {
  background: var(--paper-card);
  border: 1px solid var(--rule-soft);
  padding: 24px;
}
.group-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.group-emoji {
  font-size: 28px;
  line-height: 1;
}
.group-name {
  font-family: var(--font-display);
  font-size: 20px;
  font-weight: 500;
  letter-spacing: -0.01em;
  flex: 1;
}
.group-score {
  font-family: var(--font-mono);
  font-size: 12px;
  padding: 4px 10px;
  letter-spacing: 0.05em;
  background: var(--paper-warm);
  color: var(--ink-soft);
}
.group-score.good { background: var(--good-soft); color: #1A4A30; }
.group-score.warn { background: var(--warn-soft); color: #6B4A0F; }
.group-score.bad { background: var(--bad-soft); color: #6B1F18; }
.group-bar {
  height: 6px;
  background: var(--paper-warm);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 16px;
}
.group-bar-fill {
  height: 100%;
  border-radius: 3px;
  background: var(--neutral);
}
.group-bar-fill.good { background: var(--good); }
.group-bar-fill.warn { background: var(--warn); }
.group-bar-fill.bad { background: var(--bad); }
.group-feedback {
  font-size: 14px;
  color: var(--ink-soft);
  line-height: 1.6;
  margin: 0;
}

.portion-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 16px;
}
.portion-item {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--rule-soft);
}
.portion-amount {
  font-family: var(--font-display);
  font-size: 18px;
  font-weight: 500;
  color: var(--swiss-red);
  letter-spacing: -0.01em;
  min-width: 60px;
}
.portion-name {
  font-size: 13px;
  color: var(--ink-soft);
  line-height: 1.4;
}

.pull {
  font-family: var(--font-display);
  font-style: italic;
  font-size: 24px;
  line-height: 1.4;
  color: var(--ink);
  padding: 24px 32px;
  border-left: 2px solid var(--swiss-red);
  background: var(--paper-warm);
  margin: 24px 0;
}

.recs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.rec-card {
  background: var(--paper-card);
  border: 1px solid var(--ink);
  padding: 32px;
  position: relative;
  transition: transform 0.2s, box-shadow 0.2s;
}
.rec-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(26, 26, 26, 0.08);
}
.rec-priority {
  position: absolute;
  top: -1px;
  left: -1px;
  background: var(--swiss-red);
  color: white;
  font-family: var(--font-mono);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 6px 10px;
}
.rec-priority.medium { background: var(--warn); color: var(--ink); }
.rec-priority.low { background: var(--good); color: white; }
.rec-icon {
  width: 48px;
  height: 48px;
  margin: 24px 0 16px;
}
.rec-title {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 500;
  line-height: 1.2;
  margin: 0 0 12px;
  letter-spacing: -0.01em;
}
.rec-body {
  font-size: 14px;
  color: var(--ink-soft);
  line-height: 1.6;
  margin: 0 0 20px;
}
.rec-impact {
  font-family: var(--font-mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-mute);
  padding-top: 16px;
  border-top: 1px solid var(--rule-soft);
}
.rec-impact strong {
  color: var(--good);
  font-family: var(--font-body);
  font-weight: 500;
}

.report-footer {
  padding: 64px 0 48px;
  border-top: 1px solid var(--ink);
  background: var(--paper-warm);
  margin-top: 64px;
}
.footer-inner {
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 32px;
  flex-wrap: wrap;
}
.footer-brand p {
  font-size: 13px;
  color: var(--ink-mute);
  max-width: 360px;
  margin: 12px 0 0;
  line-height: 1.6;
}
.footer-meta {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-mute);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  text-align: right;
}
.placeholder-note {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-faint);
  text-align: center;
  margin: 32px 0 0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

@media (max-width: 900px) {
  .hero-grid { grid-template-columns: 1fr; gap: 48px; }
  .wide-narrow,
  .recs-grid,
  .group-list,
  .portion-grid { grid-template-columns: 1fr; }
  .section-header { grid-template-columns: 1fr; gap: 12px; }
  .hero-title { font-size: 48px; }
  .container,
  .topbar-inner { padding-left: 20px; padding-right: 20px; }
  .report-section { padding: 56px 0; }
}
</style>
